import { m, AnimatePresence } from "framer-motion";
import { Minus, Plus, User } from "lucide-react";
import { formatMoney, type CommissionTypeOption } from "@shared/commission";
import { useCommissionStore, draftTypes } from "../../stores/commissionStore";
import { useArtistOptions } from "../../hooks/useArtistOptions";
import { useIntakeCopy } from "../../i18n/useIntakeCopy";
import { Pressable } from "../../motion/primitives";
import { staggerChildren, riseItem, SPRING_TOUCH, DUR, EASE_ENTER } from "../../motion/tokens";
import { ChipGroup } from "../ChipGroup";
import { StepNav } from "../StepNav";
import type { IntakeArtist } from "../../types";

const sampleSrc = (url: string, w = 480) => url.replace("/upload/", `/upload/c_fill,g_auto,w_${w},h_${Math.round(w * 0.75)},q_auto,f_auto/`);

export function TypeStep({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const opts = useArtistOptions(artist)!;
  const s = useCommissionStore();
  const types = draftTypes(s);
  const chars = s.characters.slice(0, s.characterCount);
  const priceText = (t: CommissionTypeOption) =>
    t.price.kind === "flat" ? `${t.price.from ? `${c.type.from} ` : ""}${formatMoney(t.price.amount)}` : c.type.ask;
  const sharedType = s.sameType ? types[0] : null;
  const canNext = types.every(Boolean);

  return (
    <section>
      <h2 data-step-heading tabIndex={-1} className="font-(family-name:--font-display) font-normal text-3xl text-[var(--color-text)] outline-none">{c.type.question}</h2>
      <p className="mt-1 text-sm text-[var(--color-text-muted)]">{fmt(c.type.hint, { artist: artist.name })}</p>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-[var(--color-text)]">{c.type.countQuestion}</h3>
        <p className="text-sm text-[var(--color-text-muted)]">{c.type.countHint}</p>
        <div className="mt-3 flex items-center gap-4">
          <button type="button" aria-label="-" onClick={() => s.setCharacterCount(s.characterCount - 1, opts.maxCharacters)} disabled={s.characterCount <= 1}
            className="grid h-10 w-10 place-items-center rounded-full border border-[var(--color-border)] disabled:opacity-40"><Minus className="h-4 w-4" /></button>
          <div className="flex min-w-[9rem] items-center gap-1" aria-live="polite" aria-label={`${s.characterCount}`}>
            {Array.from({ length: s.characterCount }, (_, i) => (
              <m.span key={i} initial={{ scale: 0, y: 6 }} animate={{ scale: 1, y: 0 }} transition={SPRING_TOUCH}>
                <User className="h-7 w-7 text-[var(--color-primary)]" aria-hidden />
              </m.span>
            ))}
            <span className="ml-2 text-xl tabular-nums text-[var(--color-text)]">{s.characterCount}</span>
          </div>
          <button type="button" aria-label="+" onClick={() => s.setCharacterCount(s.characterCount + 1, opts.maxCharacters)} disabled={s.characterCount >= opts.maxCharacters}
            className="grid h-10 w-10 place-items-center rounded-full border border-[var(--color-border)] disabled:opacity-40"><Plus className="h-4 w-4" /></button>
        </div>

        <AnimatePresence initial={false}>
          {s.characterCount > 1 && (
            <m.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0, transition: { duration: DUR.ui, ease: EASE_ENTER } }} exit={{ opacity: 0 }} className="mt-5">
              <p className="mb-2 font-medium text-[var(--color-text)]">{c.type.sameQuestion}</p>
              <ChipGroup<"yes" | "no"> single label={c.type.sameQuestion}
                options={[{ id: "yes", label: c.type.sameYes }, { id: "no", label: c.type.sameNo }]}
                value={[s.sameType ? "yes" : "no"]} onToggle={(v) => s.setSameType(v === "yes")} />
            </m.div>
          )}
        </AnimatePresence>
      </div>

      {opts.types.length === 0 ? (
        <p className="mt-8 text-[var(--color-text-muted)]">{fmt(c.type.noTypes, { artist: artist.name })}</p>
      ) : s.sameType || s.characterCount === 1 ? (
        <m.div variants={staggerChildren()} initial="hidden" animate="show" className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {opts.types.map((t) => {
            const sample = artist.samples?.types?.[t.key];
            return (
              <m.div key={t.key} variants={riseItem}>
                <Pressable selected={sharedType === t.key} onClick={() => s.setAllTypes(t.key)}
                  className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-left hover:border-[var(--color-text-muted)]">
                  {sample && <img src={sampleSrc(sample.url)} alt={`${t.label} example by ${artist.name}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />}
                  <span className="block px-4 pt-3 font-medium text-[var(--color-text)]">{t.label}</span>
                  <span className="block px-4 pb-4 pt-1 text-sm text-[var(--color-primary)]">{priceText(t)}</span>
                </Pressable>
              </m.div>
            );
          })}
        </m.div>
      ) : (
        <div className="mt-8 space-y-4">
          {chars.map((ch, i) => (
            <div key={ch.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
              <p className="mb-2 font-medium text-[var(--color-text)]">{fmt(c.type.perCharacter, { n: i + 1 })}</p>
              <ChipGroup single label={fmt(c.type.perCharacter, { n: i + 1 })}
                options={opts.types.map((t) => ({ id: t.key, label: `${t.label} · ${priceText(t)}` }))}
                value={ch.type ? [ch.type] : []} onToggle={(v) => s.setCharacterType(ch.id, v)} />
            </div>
          ))}
        </div>
      )}

      <StepNav canNext={canNext} hideBack />
    </section>
  );
}
